import { useEffect, useRef } from 'react'

/**
 * Where a cited record lives (RFC-0016 §7: "A record citation opens the record
 * where it lives, as a document citation opens the document").
 *
 * Each surface already shows its own records, and each holds its selection in
 * local state. So a chip does not open anything new: it goes to the record's
 * surface with `?record=<id>`, and that surface selects the row it already
 * knows how to show. A pending change set has no surface of its own, so it
 * goes to Home, where proposals are listed, and opens in its drawer there.
 */
export type RecordLink =
  | { to: '/plan' | '/outcomes' | '/work' | '/context' | '/team'; search: { record: string } }
  | { to: '/'; search: { set: string } }
  | { to: '/' | '/work' | '/plan' | '/inputs' | '/outcomes' | '/team' | '/outputs' | '/context'; search: Record<string, never> }

/** A surface, as the rail keys it, to its route. A count cites the surface its rows live on. */
const SURFACE_ROUTES = {
  home: '/',
  work: '/work',
  plan: '/plan',
  inputs: '/inputs',
  outcomes: '/outcomes',
  team: '/team',
  outputs: '/outputs',
  context: '/context',
} as const

export function recordLink(citation: {
  recordId: string
  family: string
  recordType: string
}): RecordLink {
  const search = { record: citation.recordId }
  switch (citation.family) {
    case 'plan':
      return { to: citation.recordType === 'outcome' ? '/outcomes' : '/plan', search }
    // An indicator scores an outcome, and its surface is Outcomes, which opens
    // the outcome it scores. Plan lists none since UX run 08.
    case 'indicator':
      return { to: '/outcomes', search }
    case 'ledger':
    case 'workitem':
      return { to: '/work', search }
    case 'context':
      return { to: '/context', search }
    case 'stakeholder':
      return { to: '/team', search }
    case 'set':
      return { to: '/', search: { set: citation.recordId } }
    case 'surface':
      return {
        to: SURFACE_ROUTES[citation.recordType as keyof typeof SURFACE_ROUTES] ?? '/',
        search: {},
      }
    default:
      return { to: '/', search: {} }
  }
}

/** The `?record=` a surface accepts, validated rather than trusted. */
export function recordSearch(search: Record<string, unknown>): { record?: string } {
  return typeof search.record === 'string' && search.record.length > 0 ? { record: search.record } : {}
}

/**
 * The `?set=` Home accepts: a pending proposal to open in its drawer. Plan
 * names J.E.D.I.'s plan proposals as a reference and sends each here, where it
 * is decided (the routing law, B-1).
 */
export function proposalSearch(search: Record<string, unknown>): { set?: string } {
  return typeof search.set === 'string' && search.set.length > 0 ? { set: search.set } : {}
}

/**
 * Selects the linked record once its row has loaded, and ONCE: the link is a
 * door, not a binding. Without the guard, every refetch would pull the
 * selection back to the linked row after the person had moved on.
 */
export function useLinkedRecord<T>(
  record: string | undefined,
  rows: readonly T[] | undefined,
  idOf: (row: T) => string,
  select: (row: T) => void,
) {
  const applied = useRef<string | null>(null)
  useEffect(() => {
    if (!record || applied.current === record || !rows) return
    const row = rows.find((r) => idOf(r) === record)
    if (!row) return
    applied.current = record
    select(row)
  })
}
